import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Alert,
    AlertDescription,
    Progress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "frontend-components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIssuesCategoryMetrics } from "./useIssuesCategoryMetrics";
import type { IssueCategoryData } from "./useIssuesCategoryMetrics";

const CATEGORY_DOT_COLORS = [
    "bg-chameleon-pink",
    "bg-chameleon-yellow",
    "bg-chameleon-green",
    "bg-compendia-greige",
] as const;

const CATEGORY_PROGRESS_COLORS = ["pink", "yellow", "green", "greige-dark"] as const;

const getCategoryColor = (index: number): string =>
    CATEGORY_DOT_COLORS[index % CATEGORY_DOT_COLORS.length];

const getCategoryProgressColor = (index: number): (typeof CATEGORY_PROGRESS_COLORS)[number] =>
    CATEGORY_PROGRESS_COLORS[index % CATEGORY_PROGRESS_COLORS.length];

export function IssuesTable() {
    const { t } = useTranslation();
    const { tableData, isLoading, error } = useIssuesCategoryMetrics();

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<IssueCategoryData>();
        return [
            columnHelper.accessor("area", {
                header: () => t("dashboard.category").toUpperCase(),
                cell: (info) => {
                    const rowIndex = info.row.index;
                    return (
                        <div className="flex items-center gap-2 min-w-0">
                            <span
                                className={`w-2 h-2 rounded-full shrink-0 ${getCategoryColor(
                                    rowIndex,
                                )}`}
                            />
                            <span className="truncate capitalize text-[13px] text-compendia-gray-80">
                                {info.getValue<string>()}
                            </span>
                        </div>
                    );
                },
            }),
            columnHelper.accessor("deviations", {
                header: () => (
                    <span className="block text-right">{t("dashboard.count").toUpperCase()}</span>
                ),
                cell: (info) => (
                    <div className="text-right text-[15px] font-bold text-compendia-foreground">
                        {info.getValue<number>()}
                    </div>
                ),
            }),
            columnHelper.accessor("percentage", {
                header: () => (
                    <span className="block text-right">{t("dashboard.share").toUpperCase()}</span>
                ),
                cell: (info) => {
                    const rowIndex = info.row.index;
                    return (
                        <div className="flex justify-end">
                            <Progress
                                value={info.getValue<number>()}
                                color={getCategoryProgressColor(rowIndex)}
                                className="w-32"
                            />
                        </div>
                    );
                },
            }),
        ];
    }, [t]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map((column) => ({
                      ...column,
                      cell: () => (
                          <Skeleton className="bg-compendia-greige-60 w-full h-4 rounded-full" />
                      ),
                  }))
                : columns,
        [columns, isLoading],
    );

    const displayData = useMemo<IssueCategoryData[]>(
        () => (isLoading ? Array.from({ length: 6 }, () => ({}) as IssueCategoryData) : tableData),
        [tableData, isLoading],
    );

    const table = useReactTable<IssueCategoryData>({
        data: displayData,
        columns: tableColumns,
        initialState: {
            sorting: [{ id: "deviations", desc: true }],
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (error) {
        return (
            <Alert variant="error">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-chameleon-pink" />
                <AlertDescription>
                    {t("common.errorLoadingData")}: {error.message}
                </AlertDescription>
            </Alert>
        );
    }

    if (!isLoading && tableData.length === 0) {
        return (
            <div className="text-center py-6 text-[13px] text-compendia-muted">
                {t("dashboard.noIssuesFound")}
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                        key={headerGroup.id}
                        className="hover:bg-transparent border-b border-compendia-border"
                    >
                        {headerGroup.headers.map((header) => (
                            <TableHead
                                key={header.id}
                                className="h-auto py-2 px-0 text-[10px] font-semibold uppercase tracking-category text-compendia-gray-60"
                            >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                        ))}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRow
                        key={row.id}
                        className="hover:bg-transparent border-b border-compendia-border"
                    >
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="py-3 px-0 align-middle">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}
